//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, Grid, Typography } from '@mui/material'
import React, { ReactElement } from 'react'

import { EthAddr } from '@shared/SchemaTypes'

import { useOtDispatch, useOtSelector } from '@react/redux/store'

import { RolloverTypeEnum } from '@shared/Enums'
import { PoolOverviewCTO, PoolOverviewCTOSchema } from '@shared/sharedTypes'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { erc20WeiToCommaDecString } from '@shared/conversions'
import ViewField from '@react/components/elements/ViewField'
import { theme } from '@react/components/theme'
import { initiateRolloverTx } from '@react/signedTxs/initiateRolloverTx'

export default function AdminInitiateRolloverForm(props: { poolAddr: EthAddr }): ReactElement {
  const { poolAddr } = props
  const dispatch = useOtDispatch()

  const poolOverviewCTO = useOtSelector(
    (state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO] as PoolOverviewCTO
  )

  const [isSubmitted, setIsSubmitted] = React.useState(false)
  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    borderRadius: '6px',
  }

  const initiateRolloverAction = (): void => {
    setIsSubmitted(true)
    dispatch(
      initiateRolloverTx({
        poolAddr,
        poolControllerAddr: poolOverviewCTO.poolControllerAddr,
        loanAddr: poolOverviewCTO.requestedLoanState.loanAddr,
        priorLoanAddr: poolOverviewCTO.activeLoanState.loanAddr,
      })
    )
  }

  const TitleContent = () => {
    if (poolOverviewCTO.rolloverType === RolloverTypeEnum.NetAdditionalDeposits) {
      return (
        <>
          <Grid item xs={12}>
            <Typography variant="bodyMuted" gutterBottom component="div">
              Net Additional Deposits
            </Typography>
            <Typography variant="bodyBold" gutterBottom component="div">
              {erc20WeiToCommaDecString(poolOverviewCTO.rolloverAssetToGetFromPool)}{' '}
              {poolOverviewCTO.liquidityTokenSymbol}
            </Typography>
          </Grid>
        </>
      )
    } else if (poolOverviewCTO.rolloverType === RolloverTypeEnum.NeedsFullRepayment) {
      return (
        <>
          <Grid item xs={12}>
            <Typography variant="bodyMuted" gutterBottom component="div">
              Full Repayment
            </Typography>
            <Typography variant="bodyBold" gutterBottom component="div">
              {erc20WeiToCommaDecString(poolOverviewCTO.rolloverNetRepaymentWithFees)}{' '}
              {poolOverviewCTO.liquidityTokenSymbol}
            </Typography>
          </Grid>
        </>
      )
    } else {
      return (
        <>
          <Grid item xs={12}>
            <Typography variant="bodyMuted" gutterBottom component="div">
              Partial Repayment
            </Typography>
            <Typography variant="bodyBold" gutterBottom component="div">
              {erc20WeiToCommaDecString(poolOverviewCTO.rolloverNetRepaymentWithFees)}{' '}
              {poolOverviewCTO.liquidityTokenSymbol}
            </Typography>
          </Grid>
        </>
      )
    }
  }
  return (
    <Box
      data-testid={AdminInitiateRolloverForm.name}
      px={0}
      py={0}
      sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', direction: 'column', paddingTop: 4 }}
    >
      <Grid container spacing={3}>
        <TitleContent />
        <Grid item xs={12}>
          <Typography variant="bodyMuted" gutterBottom component="div">
            Repayment of Fees
          </Typography>
          <Typography variant="bodyBold" gutterBottom component="div">
            {erc20WeiToCommaDecString(poolOverviewCTO.rolloverMaturingLoanFees)} {poolOverviewCTO.liquidityTokenSymbol}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <ViewField
            dataObject={poolOverviewCTO}
            fieldSchema={PoolOverviewCTOSchema.describe().fields.rolloverOpeningLoanPrincipal}
          ></ViewField>
        </Grid>

        <Grid item xs={12}>
          <Button
            disabled={isSubmitted}
            sx={sx}
            onClick={() => {
              initiateRolloverAction()
            }}
            variant={'contained'}
          >
            <Typography variant={'buttonBold'}>Initiate Rollover</Typography>
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}
